// v3/shared/types/domain.ts — Core domain enums and entity interfaces
//
// These interfaces are verified against the actual backend service models:
// - ApplicationManagementService (ApplicationEntity, AddressEntity, BankAccountEntity)
// - ContactManagementService (Contact, GroupResponse, GroupMemberDetails)
// - TaskManagementService (TasksEntity, TaskDetailsResponse)
// - NoteKeeperService (NotesEntity, NotesQueryResponse)
// - KYCAndDocumentService (DocumentEntity)

// ─── Enums ───────────────────────────────────────────────────────────────────

export enum Role {
  ADMIN = 'ADMIN',
  SALES_USER = 'SALES_USER',
  BACK_OFFICE_USER = 'BACK_OFFICE_USER',
  FIELD_USER = 'FIELD_USER',
  SALES_MANAGER = 'SALES_MANAGER',
  MANAGER = 'MANAGER',
}

export enum ProductType {
  PERSONAL_LOAN = 'PERSONAL_LOAN',
  BUSINESS_LOAN = 'BUSINESS_LOAN',
  CONSUMER_DURABLES_LOAN = 'CONSUMER_DURABLES_LOAN',
  GROUP_LOAN = 'GROUP_LOAN',
}

export enum ApplicationState {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  SOFT_APPROVED = 'SOFT_APPROVED',
  PENDING_VERIFICATION = 'PENDING_VERIFICATION',
  VERIFICATION_COMPLETE = 'VERIFICATION_COMPLETE',
  /** Group loans: submit lands here; back office proposes terms, sends back for edits, or rejects (AMS CreditReviewState). */
  CREDIT_REVIEW = 'CREDIT_REVIEW',
  /** Group loans: sales accepts or counters the proposed terms; both submit the application (AMS SalesReviewState). */
  SALES_REVIEW = 'SALES_REVIEW',
  APPROVED = 'APPROVED',
  INVOICE_COLLECTED = 'INVOICE_COLLECTED',
  MANDATED = 'MANDATED',
  SIGNED = 'SIGNED',
  DISBURSED = 'DISBURSED',
  REJECTED = 'REJECTED',
}

export enum Action {
  VIEW_APPLICATION = 'VIEW_APPLICATION',
  CREATE_APPLICATION = 'CREATE_APPLICATION',
  EDIT_APPLICATION = 'EDIT_APPLICATION',
  SAVE_APPLICATION = 'SAVE_APPLICATION',
  SUBMIT_APPLICATION = 'SUBMIT_APPLICATION',
  UPLOAD_DOCUMENT = 'UPLOAD_DOCUMENT',
  SOFT_APPROVE = 'SOFT_APPROVE',
  REQUEST_EDIT_APPLICATION = 'REQUEST_EDIT_APPLICATION',
  REJECT_APPLICATION = 'REJECT_APPLICATION',
  LOCK_APPLICATION = 'LOCK_APPLICATION',
  SANCTION = 'SANCTION',
  START_VERIFICATION = 'START_VERIFICATION',
  VERIFICATION_COMPLETE = 'VERIFICATION_COMPLETE',
  START_CREDIT_REVIEW = 'START_CREDIT_REVIEW',
  PROPOSE_TERMS = 'PROPOSE_TERMS',
  ACCEPT_TERMS = 'ACCEPT_TERMS',
  COUNTER_TERMS = 'COUNTER_TERMS',
  INVOICE_COLLECTED = 'INVOICE_COLLECTED',
  MANDATE = 'MANDATE',
  SIGN = 'SIGN',
  DISBURSE = 'DISBURSE',
  REOPEN_APPLICATION = 'REOPEN_APPLICATION',
  VIEW_USER_LOGIN_HISTORY = 'VIEW_USER_LOGIN_HISTORY',
}

export enum TaskStatus {
  OPEN = 'OPEN',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETE = 'COMPLETE',
  CANCELLED = 'CANCELLED',
}

export enum TaskType {
  ACTION = 'ACTION',
  TICKET = 'TICKET',
  APPROVAL = 'APPROVAL',
  SIMPLE_TASK = 'SIMPLE_TASK',
  VERIFICATION = 'VERIFICATION',
  LEAD_FOLLOW_UP = 'LEAD_FOLLOW_UP',
  PRE_APPROVED_DOCUMENT_COLLECTION = 'PRE_APPROVED_DOCUMENT_COLLECTION',
}

// ─── Lead / Contact ──────────────────────────────────────────────────────────
// Source: ContactManagementService — Contact.java, ContactResponse.java, ContactV2Response.java

/** Contact entity as stored in DynamoDB (lead_contacts table) */
export interface Lead {
  id?: string;
  phoneNumber: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  email?: string;
  /** Address object or JSON string depending on API version */
  address?: string | { addressLineOne?: string; addressLineTwo?: string; city?: string; state?: string; postalCode?: string };
  /** Source object or JSON string */
  source?: string | { referral?: string; channel?: string };
  pilId?: string;
  city?: string;
  /** Loan requirements returned by the API */
  loanRequirements?: { amount?: string; tenure?: string; rateOfInterest?: string };
  /** Metadata — can be JSON string or object. Key is lowercase 'd' from backend. */
  metadata?: string | Record<string, unknown>;
  /** Alias for metadata (some responses use camelCase) */
  metaData?: string | Record<string, unknown>;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
  isDeleted?: boolean;
}

/** V2 search response summary (POST /v2/contact/search) */
export interface LeadSummary {
  id: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email?: string;
  city?: string;
  source?: { referral: string; channel: string };
  pilId?: string;
  metadata?: string;
}

// ─── Application ─────────────────────────────────────────────────────────────
// Source: ApplicationManagementService — ApplicationEntity.java, ApplicationResponse.java

/** Application Lock — blocks EDIT actions but allows transitions (e.g. group-loan offer decline). */
export interface Lock {
  locked: boolean;
  reason?: string | null;
  detail?: string | null;
  lockedBy?: string | null;
  lockedAt?: string | null;
}

export interface Application {
  id: string;
  applicationId?: string;
  leadId: string;
  state: ApplicationState;
  productType: string;
  createdBy: string;
  lastModifiedBy?: string;
  personalDetails: PersonalDetails;
  kycDetails: KycDetails;
  loanRequirementDetails: LoanDetails;
  loanApprovalDetails?: LoanApprovalDetails;
  rejectionDetails?: RejectionDetails;
  incomeDetails: IncomeDetails;
  membershipDetails?: PidiliteMembershipDetails;
  groupDetails?: GroupDetails;
  /** Stored as JSON string */
  existingLoans?: string;
  /** Array of address IDs (not full objects) */
  addresses: string[];
  coApplicantDetails: CoApplicantDetails[];
  references: ReferenceDetails[];
  createdAt?: string;
  updatedAt?: string;
  // Summary-only fields (from /application/summary)
  customerName?: string;
  product?: string;
  /** eNACH UMRN, set once the mandate is REGISTERED. */
  umrn?: string;
  lock?: Lock;
}

export interface PersonalDetails {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  fatherName?: string;
  motherName?: string;
  spouseName?: string;
  phoneNumber?: string;
  email?: string;
  nationality?: string;
  maritalStatus?: string;
  gender?: string;
  dob?: string;
  occupation?: string;
  education?: string;
  religion?: string;
  caste?: string;
}

export interface KycDetails {
  panNumber?: string;
  aadharNumber?: string;
  voterIdNumber?: string;
  passportNumber?: string;
  drivingLicenseNumber?: string;
  /** documentType → kycId of the verified record the underwriter relied on. */
  kycRecordIds?: Record<string, string>;
}

export interface LoanDetails {
  amount?: number;
  rateOfInterest?: number;
  tenureInMonths?: number;
  loanPurpose?: string;
}

export interface IncomeDetails {
  monthlyIncome?: number;
  otherMonthlyIncome?: number;
  assets?: string[];
}

export interface LoanApprovalDetails {
  rateOfInterest?: number;
  amount?: number;
  tenureInMonths?: number;
  bureauScore?: number;
  softApprovalDate?: string;
  approvalDate?: string;
  approvedBy?: string;
  sanctionedBy?: string;
  rejectedDate?: string;
  rejectedBy?: string;
  rejectionReason?: string;
}

export interface RejectionDetails {
  rejectReason?: string;
  detailedReason?: string;
  rejectedDate?: string;
  rejectedBy?: string;
}

export interface PidiliteMembershipDetails {
  memberId?: string;
  memberName?: string;
  category?: string;
  enrollmentDate?: string;
  clubName?: string;
  clubId?: string;
  bdName?: string;
  bdPhoneNumber?: string;
  memberState?: string;
}

export interface GroupDetails {
  id?: string;
  name?: string;
  city?: string;
}

export interface CoApplicantDetails {
  personalDetails: PersonalDetails;
  kycDetails: KycDetails;
  /** Array of address IDs */
  addressId: string[];
  relationToApplicant?: string;
}

export interface ReferenceDetails {
  addressId?: string;
  relationToApplicant?: string;
  personalDetails: PersonalDetails;
}

// ─── Address ─────────────────────────────────────────────────────────────────
// Source: ApplicationManagementService — AddressEntity.java, AddressDto.java

export interface Address {
  id?: string;
  /** DynamoDB hash key (resourceId) */
  applicationId?: string;
  leadId?: string;
  type?: string;
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  country?: string;
  /** Integer in backend */
  postalCode?: number;
  /** Double in backend */
  latitude?: number;
  /** Double in backend */
  longitude?: number;
  active?: boolean;
  verified?: boolean;
  landmark?: string;
  ownership?: string;
  /** Integer in backend */
  monthlyRent?: number;
  /** Integer in backend */
  yearsOfStay?: number;
}

// ─── Bank Account ────────────────────────────────────────────────────────────
// Source: ApplicationManagementService — BankAccountEntity.java, BankAccountDto.java

export interface BankAccount {
  id?: string;
  /** DynamoDB hash key (resourceId) */
  applicationId?: string;
  leadId?: string;
  bankName?: string;
  ifsc?: string;
  branchName?: string;
  customerId?: string;
  accountNumber?: string;
  accountType?: string;
  isRepaymentAccount?: boolean;
  bankDocumentType?: string;
  bankDocumentId?: string;
}

// ─── Task ────────────────────────────────────────────────────────────────────
// Source: TaskManagementService — TasksEntity.java, TaskDetailsResponse.java

export interface Task {
  taskId: string;
  tenantId?: string;
  assignee: string;
  author: string;
  status: TaskStatus;
  /** TaskType enum: ACTION, TICKET, APPROVAL, SIMPLE_TASK, VERIFICATION, LEAD_FOLLOW_UP, PRE_APPROVED_DOCUMENT_COLLECTION */
  type: string;
  title: string;
  description: string;
  /** Integer in backend */
  priority: number;
  /** Long in backend (epoch millis) */
  dueDateInUTCEpoch: number;
  createDateInUTCEpoch: number;
  updateDateInUTCEpoch: number;
  updatedBy?: string;
  /** tenantResourceId in entity */
  resourceId?: string;
  /** tenantResourceType in entity */
  resourceType?: string;
  closureReason?: string;
  watchers?: string[];
  labels?: string[];
  isBlocked?: boolean;
  contextData?: Record<string, string>;
}

// ─── Group ───────────────────────────────────────────────────────────────────
// Source: ContactManagementService — GroupResponse.java, GroupMemberDetails.java

export interface Group {
  id: string;
  name: string;
  city: string;
  /** Full member object (NOT just coordinatorId) */
  coordinator: GroupMemberDetails;
  /** Nested object (NOT flat fields) */
  bdManager: BDManager;
  members: GroupMemberDetails[];
  metaData?: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

export interface GroupMemberDetails {
  contact: ContactResponseDetails;
  status: string;
  role: 'COORDINATOR' | 'MEMBER';
  joinedAt?: string;
}

export interface ContactResponseDetails {
  id: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email?: string;
}

export interface BDManager {
  name: string;
  phone: string;
}

// ─── Note ────────────────────────────────────────────────────────────────────
// Source: NoteKeeperService — NotesEntity.java, NotesQueryResponse.java

export interface Note {
  resourceId: string;
  resourceType: string;
  notes: string;
  /** Long (epoch millis) in backend — NOT a string */
  createDateTime: number;
  author: string;
}

// ─── Document ────────────────────────────────────────────────────────────────
// Source: KYCAndDocumentService — DocumentEntity.java

export interface Document {
  documentId: string;
  resourceType: string;
  resourceId: string;
  /** Category: APPLICATION, PAN, AADHAR, TASK_ATTACHMENTS, etc. */
  documentType: string;
  originalFilename?: string;
  mimeType?: string;
  fileSize?: number;
  active?: boolean;
  verificationStatus?: string;
  verificationRemarks?: string;
  ocrClassification?: string;
  ocrConfidence?: number;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
}

// ─── User Profile ────────────────────────────────────────────────────────────

export interface UserProfile {
  userName: string;
  email: string;
  role?: Role;
  /** False when no system role was found in Cognito — role is a fallback default */
  roleAssigned?: boolean;
}
